import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addUserMessage,
  askAi,
  clearChat,
  getChatMessages,
  getChatStatus,
} from "../redux/features/chatSlice";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import AgentIcon from "./AgentIcon";

export default function ChatPanel() {
  const dispatch = useDispatch();
  const messages = useSelector(getChatMessages);
  const status = useSelector(getChatStatus);

  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  // ---- autoscroll
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, status]);

  const ExcelButton = ({ name, b64 }) => {
    if (!b64 || typeof b64 !== "string") return null;
    // Descarga por Blob (los data: URLs grandes fallan en algunos navegadores).
    const handleDownload = () => {
      try {
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name || "resultado.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
      } catch (e) {
        console.error("No se pudo descargar el Excel:", e);
      }
    };
    return (
      <button
        type="button"
        onClick={handleDownload}
        className="mt-2 inline-flex items-center justify-center rounded-full px-4 py-2 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition"
      >
        Descargar Excel
      </button>
    );
  };

  // ---- celda con truncado + tooltip
  const Cell = ({ value }) => {
    let s;
    if (value == null) {
      s = "";
    } else if (typeof value === "object" && !Array.isArray(value)) {
      // objeto anidado (ej: persona) — mostrar campos relevantes
      const parts = [];
      if (value.nombre) parts.push(value.nombre);
      if (value.apellidos) parts.push(value.apellidos);
      if (value.ci) parts.push(`CI:${value.ci}`);
      if (value.sexo) parts.push(value.sexo);
      if (value.telf1) parts.push(value.telf1);
      s = parts.length ? parts.join(" · ") : JSON.stringify(value).slice(0, 80);
    } else if (Array.isArray(value)) {
      s = value.map(v => typeof v === "object" ? JSON.stringify(v) : String(v)).join(", ");
    } else {
      s = String(value);
    }
    const short = s.length > 60 ? s.slice(0, 57) + "…" : s;
    return (
      <span className="block max-w-[260px] truncate" title={s}>
        {short}
      </span>
    );
  };

  // ---- Tabla dentro de burbuja (scroll horizontal + sticky header)
  const Table = ({ title, columns, rows, total }) => {
    if (!columns?.length || !rows?.length) return null;

    return (
      <div className="mt-2">
        {title ? (
          <div className="text-xs font-semibold text-gray-700 mb-2">
            {title}
            {typeof total === "number" ? (
              <span className="text-gray-500 font-normal"> · Total: {total}</span>
            ) : null}
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-xl border border-black/10 bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-black/[0.04] sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c}
                    className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-black/10 hover:bg-black/[0.02]">
                  {columns.map((c) => (
                    <td key={c} className="px-3 py-2 align-top">
                      <Cell value={r?.[c]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ---- KPI (ui.type=stat)
  const Stat = ({ title, value }) => (
    <div className="mt-2 rounded-xl border border-black/10 bg-white px-4 py-3">
      <div className="text-xs text-gray-500">{title || "Resultado"}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );

  // ---- Error (ui.type=error)
  const ErrorBox = ({ message, tool }) => (
    <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
      <div className="text-xs font-semibold text-red-700">
        Error{tool ? ` (${tool})` : ""}
      </div>
      <div className="text-sm text-red-800 whitespace-pre-wrap">{message}</div>
    </div>
  );

  // ---- Lista simple (ui.type=list)
  const ListBox = ({ title, items }) => (
    <div className="mt-2 rounded-xl border border-black/10 bg-white px-4 py-3">
      {title ? <div className="text-xs font-semibold text-gray-700 mb-2">{title}</div> : null}
      <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
        {(items || []).slice(0, 30).map((it, idx) => (
          <li key={idx} className="break-words">
            {typeof it === "string" ? it : JSON.stringify(it)}
          </li>
        ))}
      </ul>
    </div>
  );

  // ---- Renderizador principal por mensaje
  const MessageBody = ({ m }) => {
    // 1) Si viene ui del backend, lo priorizamos
    const ui = m.ui;

    const hasLegacyTable = m.columns?.length && m.rows?.length;
    const hasUiTable = ui?.type === "table" && ui.columns?.length && ui.rows?.length;

    // 2) Markdown content: lo renderizamos SIEMPRE si existe
    const mdText = (m.content && m.content.trim()) || "";

    return (
      <div className="space-y-2">
        {/* Markdown */}
        {mdText ? (
          <div className="prose prose-sm max-w-none prose-p:my-1 prose-pre:my-2 prose-table:my-2">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                // tablas markdown -> scroll
                table: ({ children }) => (
                  <div className="overflow-x-auto rounded-xl border border-black/10 bg-white">
                    <table className="min-w-full text-xs">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-black/[0.04]">{children}</thead>,
                th: ({ children }) => (
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">{children}</th>
                ),
                td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
                pre: ({ children }) => (
                  <pre className="overflow-x-auto rounded-xl bg-black text-white p-3 text-xs">
                    {children}
                  </pre>
                ),
                code: ({ inline, children }) =>
                  inline ? (
                    <code className="rounded bg-black/[0.05] px-1 py-0.5 text-xs">
                      {children}
                    </code>
                  ) : (
                    <code className="text-xs">{children}</code>
                  ),
              }}
            >
              {mdText}
            </ReactMarkdown>
          </div>
        ) : null}

        {/* UI render (preferente) */}
        {ui?.type === "stat" ? <Stat title={ui.title} value={ui.value} /> : null}
        {ui?.type === "error" ? <ErrorBox tool={ui.tool} message={ui.message} /> : null}
        {ui?.type === "list" ? <ListBox title={ui.title} items={ui.items} /> : null}
        {ui?.type === "empty" ? (
          <div className="text-sm text-gray-600">{ui.title ? `${ui.title}: ` : ""}Sin resultados.</div>
        ) : null}

        {hasUiTable ? (
          <Table
            title={ui.title}
            total={ui.total}
            columns={ui.columns}
            rows={ui.rows}
          />
        ) : null}

        {/* Compat: tabla vieja columns/rows */}
        {!hasUiTable && hasLegacyTable ? (
          <Table title="Resultado" columns={m.columns} rows={m.rows} />
        ) : null}

        {/* Excel */}
        {m.excelBytes ? <ExcelButton name={m.excelName} b64={m.excelBytes} /> : null}
      </div>
    );
  };

  const onSend = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || status === "loading") return;

    dispatch(addUserMessage(q));
    dispatch(askAi(q));
    setInput("");
  };

  // ---- burbuja estilo messenger
  const Bubble = ({ role, children }) => {
    const isUser = role === "user";

    return (
      <div className={`w-full flex ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && <AgentIcon className="w-8 h-8 mr-2 mt-1" />}

        <div
          className={[
            "max-w-[78%] px-4 py-2 shadow-sm",
            "rounded-2xl",
            isUser
              ? "bg-[#f7b500] text-white rounded-br-md"
              : "bg-white text-gray-900 border border-black/10 rounded-bl-md",
          ].join(" ")}
        >
          {/* Para que Markdown/tables no se “salgan” */}
          <div className="min-w-0">{children}</div>
        </div>

        {isUser && (
          <img
            src="/images/profile.png"
            alt="Tú"
            className="w-8 h-8 rounded-full ml-2 mt-1 border border-black/10"
          />
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* header tipo messenger */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AgentIcon className="w-8 h-8" />
          <div className="leading-tight">
            <div className="text-sm font-semibold text-gray-900">Asistente</div>
            <div className="text-xs text-gray-500">Responde con datos y archivos</div>
          </div>
        </div>

        <button
          className="text-xs text-gray-500 hover:text-gray-700"
          onClick={() => dispatch(clearChat())}
          type="button"
        >
          Limpiar
        </button>
      </div>

      {/* cuerpo chat */}
      <div
        ref={scrollRef}
        className="w-full h-[420px] rounded-2xl border border-black/10 bg-[#f0f2f5] p-3 overflow-y-auto space-y-3"
      >
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role}>
            <MessageBody m={m} />
          </Bubble>
        ))}

        {status === "loading" && (
          <Bubble role="assistant">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span>Escribiendo</span>
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.2s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.1s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
              </span>
            </div>
          </Bubble>
        )}
      </div>

      {/* input bar tipo messenger */}
      <form onSubmit={onSend} className="mt-3 flex items-center gap-2">
        <div className="flex-1 bg-[#f0f2f5] rounded-full px-4 py-3 border border-black/10">
          <input
            type="text"
            className="w-full bg-transparent outline-none text-sm"
            placeholder="Escribe un mensaje…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={status === "loading"}
          className="rounded-full px-5 py-3 bg-[#f7b500] text-white text-sm font-semibold hover:brightness-95 disabled:opacity-60"
        >
          Enviar
        </button>
      </form>
    </div>
  );
}
